import { LinearProgress } from "@mui/material"
import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from "react"

export interface TickerContext {
  available: boolean
  now: number
}
export const TickerContext = createContext<TickerContext>({
  available: false,
  now: 0,
})

export interface TickerProviderProps {
  rate?: number
}
export const TickerProvider: React.FC<PropsWithChildren<TickerProviderProps>> = ({ rate = 250, children }) => {
  const [now, setCount] = useState(new Date().valueOf())
  useEffect(() => {
    const timer = setInterval(() => {
      setCount(new Date().valueOf())
    }, rate)
    return () => {
      clearInterval(timer)
    }
  }, [rate])
  return <TickerContext.Provider value={{ available: true, now }}>{children}</TickerContext.Provider>
}
export const useTicker = (): TickerContext => {
  return useContext(TickerContext)
}

export const TimeSince: React.FC<{ since: Date }> = ({ since }) => {
  const { now } = useTicker()
  const duration = now - since.valueOf()
  if (duration < 0) {
    return "0:00"
  }
  const hours = Math.floor(duration / 1000 / 60 / 60)
  const minutes = Math.floor(duration / 1000 / 60) - hours * 60
  const seconds = Math.floor(duration / 1000) - (minutes + hours * 60) * 60
  return `${hours > 0 ? `${hours}:` : ""}${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
}

export const TimeUntil: React.FC<{ until: Date }> = ({ until }) => {
  const { now } = useTicker()
  const duration = until.valueOf() - now
  if (duration < 0) {
    return "0:00"
  }
  const hours = Math.floor(duration / 1000 / 60 / 60)
  const minutes = Math.floor(duration / 1000 / 60) - hours * 60
  const seconds = Math.floor(duration / 1000) - (minutes + hours * 60) * 60
  return `${hours > 0 ? `${hours}:` : ""}${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
}

export const FormatDuration: React.FC<{ duration: number }> = ({ duration }) => {
  if (duration < 0) {
    return "0:00"
  }
  const hours = Math.floor(duration / 1000 / 60 / 60)
  const minutes = Math.floor(duration / 1000 / 60) - hours * 60
  const seconds = Math.floor(duration / 1000) - (minutes + hours * 60) * 60
  return `${hours > 0 ? `${hours}:` : ""}${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
}

export const LinearProgressStartEnd: React.FC<{ start: Date; end: Date }> = ({ start, end }) => {
  const { now } = useTicker()
  const value = useMemo(
    () => Math.floor((100 * (now - start.valueOf())) / (end.valueOf() - start.valueOf())),
    [now, start, end]
  )
  return <LinearProgress variant="determinate" value={value} />
}
